import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Building, Calendar, FileText, Briefcase, Users, Heart,
    AlertCircle, CheckCircle, Loader, Send, Edit2, IdCard, MapPin, Bed, Bath
} from 'lucide-react';
import { rawRequest } from '../../services/apiClient';
import profileService from '../../services/profileService';
import applicationService from '../../services/applicationService';
import metadataService from '../../services/metadataService';
import { getPropertyTypeName } from '../../constants/propertyConstants';
import { useNotification } from '../../App';
import SEO from '../../Components/Common/SEO';

const REQUIREMENT_LABELS = {
    national_id: 'National ID',
    proof_of_income: 'Proof of Income',
    employment_letter: 'Employment Letter',
    previous_landlord_reference: 'Previous Landlord Reference',
    bank_statement: 'Bank Statement'
};

const PropertyApplication = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const { addNotification } = useNotification();

    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const [profile, setProfile] = useState(null);
    const [requirements, setRequirements] = useState([]);

    const [form, setForm] = useState({
        desiredMoveInDate: '',
        desiredLeaseLength: '',
        desiredRentAmount: '',
        message: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            setNotFound(false);
            try {
                const data = await rawRequest(`/properties/${propertyId}`);
                if (cancelled) return;
                setProperty(data.property);
                setForm((f) => ({
                    ...f,
                    desiredLeaseLength: data.property.minimumLeaseTerm || 12,
                    desiredRentAmount: data.property.pricePerMonth || ''
                }));
            } catch {
                if (!cancelled) setNotFound(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        (async () => {
            try {
                const data = await profileService.getProfile();
                if (cancelled) return;
                setProfile(data);
                if (data.tenantProfile?.preferredMoveInDate) {
                    setForm((f) => ({ ...f, desiredMoveInDate: data.tenantProfile.preferredMoveInDate }));
                }
            } catch {
                // Profile is a nice-to-have prefill - leave the form blank if it fails
            }
        })();

        (async () => {
            try {
                const data = await metadataService.getApplicationRequirements();
                if (!cancelled) setRequirements(data);
            } catch {
                if (!cancelled) setRequirements([]);
            }
        })();

        return () => { cancelled = true; };
    }, [propertyId]);

    const propertyTypeName = property ? getPropertyTypeName(property.propertyTypeId) : '';
    const title = property ? (property.propertyName || `${property.bedrooms} Bedroom ${propertyTypeName}`) : '';

    const profileIncomplete = profile && !(profile.occupation && profile.employer && profile.tenantProfile?.monthlyIncomeRange);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);

        if (!form.desiredMoveInDate || !form.desiredLeaseLength || !form.desiredRentAmount) {
            setSubmitError('Please fill in move-in date, lease length, and rent amount.');
            return;
        }

        setSubmitting(true);
        try {
            await applicationService.applyToProperty(propertyId, {
                desiredMoveInDate: form.desiredMoveInDate,
                desiredLeaseLength: Number(form.desiredLeaseLength),
                desiredRentAmount: Number(form.desiredRentAmount),
                message: form.message.trim()
            });
            setSubmitted(true);
            addNotification('Application submitted!', 'success');
        } catch (err) {
            setSubmitError(err.message || 'Failed to submit application. Please try again.');
            addNotification('Failed to submit application', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-karibu-100 pt-20 flex items-center justify-center">
                <Loader className="w-10 h-10 text-highland-500 animate-spin" />
            </div>
        );
    }

    if (notFound || !property) {
        return (
            <div className="min-h-screen bg-karibu-100 pt-20 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-16 h-16 text-murram-500 mx-auto mb-4" />
                    <h2 className="font-display text-2xl font-bold text-soil-900 mb-2">Property Not Found</h2>
                    <p className="text-soil-600 mb-6">This listing doesn't exist or has been removed.</p>
                    <button
                        onClick={() => navigate('/listings')}
                        className="bg-highland-500 hover:bg-highland-600 text-white px-6 py-3 rounded-lg font-medium"
                    >
                        Back to Listings
                    </button>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-karibu-100 pt-20 flex items-center justify-center px-4">
                <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-highland-50 mb-6">
                        <CheckCircle className="h-8 w-8 text-highland-500" />
                    </div>
                    <h2 className="font-display text-2xl font-bold text-soil-900 mb-2">Application Submitted!</h2>
                    <p className="text-soil-600 mb-6">
                        Your application for {title} has been sent to the landlord. You'll be notified once they respond.
                    </p>
                    <div className="space-y-3">
                        <button
                            onClick={() => navigate('/applications')}
                            className="w-full bg-highland-500 hover:bg-highland-600 text-white py-3 px-4 rounded-lg font-medium transition-colors duration-200"
                        >
                            View My Applications
                        </button>
                        <button
                            onClick={() => navigate('/listings')}
                            className="w-full border border-soil-100 text-soil-600 hover:bg-soil-50 py-3 px-4 rounded-lg font-medium transition-colors duration-200"
                        >
                            Browse More Properties
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-karibu-100 pt-20 pb-8">
            <SEO title={`Apply — ${title}`} canonicalPath={`/apply/${propertyId}`} noindex />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <button
                    onClick={() => navigate(`/property/${propertyId}`)}
                    className="flex items-center space-x-2 text-soil-600 hover:text-soil-900 mb-6"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>Back to property</span>
                </button>

                <h1 className="font-display text-3xl font-bold text-soil-900 mb-2">Apply for this Property</h1>
                <p className="text-soil-600 mb-8">Tell the landlord a bit about your plans and you're done.</p>

                {/* Property summary */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6 flex items-center space-x-4">
                    <div className="w-14 h-14 rounded-lg bg-highland-50 flex items-center justify-center flex-shrink-0">
                        <Building className="w-7 h-7 text-highland-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-lg font-semibold text-soil-900 truncate">{title}</h2>
                        <div className="flex items-center space-x-4 text-sm text-soil-600 mt-1">
                            {property.city && (
                                <span className="flex items-center"><MapPin className="w-3.5 h-3.5 mr-1" />{property.city}</span>
                            )}
                            <span className="flex items-center"><Bed className="w-3.5 h-3.5 mr-1" />{property.bedrooms}</span>
                            <span className="flex items-center"><Bath className="w-3.5 h-3.5 mr-1" />{property.bathrooms}</span>
                        </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                        <div className="font-mono text-lg font-bold text-highland-500">
                            KES {Number(property.pricePerMonth).toLocaleString()}
                        </div>
                        <div className="text-xs text-soil-400">per month</div>
                    </div>
                </div>

                {/* Applicant snapshot */}
                {profile && (
                    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-display text-lg font-semibold text-soil-900">Your Details</h2>
                            <button
                                onClick={() => navigate('/profile')}
                                className="flex items-center text-sm font-medium text-highland-500 hover:text-highland-600"
                            >
                                <Edit2 className="w-3.5 h-3.5 mr-1" />
                                Edit Profile
                            </button>
                        </div>

                        {profileIncomplete && (
                            <div className="mb-4 p-3 bg-dawn-100/40 border border-dawn-100 rounded-lg flex items-start space-x-2">
                                <AlertCircle className="h-4 w-4 text-dawn-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-dawn-700">
                                    Completing your occupation, employer, and income range on your profile helps landlords review your application faster.
                                </p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                                <Briefcase className="w-4 h-4 text-soil-400" />
                                <span className="text-soil-600">{profile.occupation || 'Occupation not set'}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <IdCard className="w-4 h-4 text-soil-400" />
                                <span className="text-soil-600">{profile.employer || 'Employer not set'}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Users className="w-4 h-4 text-soil-400" />
                                <span className="text-soil-600">
                                    {profile.tenantProfile?.householdSize ? `${profile.tenantProfile.householdSize} in household` : 'Household size not set'}
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Heart className="w-4 h-4 text-soil-400" />
                                <span className="text-soil-600">{profile.tenantProfile?.pets ? 'Has pets' : 'No pets'}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Application form */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <h2 className="font-display text-lg font-semibold text-soil-900 mb-4">Application Details</h2>

                    {submitError && (
                        <div className="mb-4 p-3 bg-murram-100/40 border border-murram-100 rounded-lg flex items-center space-x-2">
                            <AlertCircle className="w-4 h-4 text-murram-500 flex-shrink-0" />
                            <p className="text-sm text-murram-600">{submitError}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-soil-600 mb-1">
                                    Desired Move-in Date
                                </label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-soil-400" />
                                    <input
                                        type="date"
                                        required
                                        min={new Date().toISOString().split('T')[0]}
                                        value={form.desiredMoveInDate}
                                        onChange={(e) => setForm((f) => ({ ...f, desiredMoveInDate: e.target.value }))}
                                        className="w-full pl-10 pr-3 py-2 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-soil-600 mb-1">
                                    Desired Lease Length (months)
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    required
                                    value={form.desiredLeaseLength}
                                    onChange={(e) => setForm((f) => ({ ...f, desiredLeaseLength: e.target.value }))}
                                    className="w-full px-3 py-2 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-soil-600 mb-1">
                                Proposed Monthly Rent (KES)
                            </label>
                            <input
                                type="number"
                                min="0"
                                required
                                value={form.desiredRentAmount}
                                onChange={(e) => setForm((f) => ({ ...f, desiredRentAmount: e.target.value }))}
                                className="w-full px-3 py-2 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-soil-600 mb-1">
                                Message to Landlord (optional)
                            </label>
                            <textarea
                                rows={4}
                                value={form.message}
                                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                                className="w-full px-3 py-2 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                placeholder="Tell the landlord a bit about yourself..."
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full flex items-center justify-center space-x-2 bg-highland-500 hover:bg-highland-600 text-white py-3 px-4 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                        >
                            {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            <span>{submitting ? 'Submitting...' : 'Submit Application'}</span>
                        </button>
                    </form>
                </div>

                {/* Document requirements */}
                {requirements.length > 0 && (
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h2 className="font-display text-lg font-semibold text-soil-900 mb-2">Be Ready to Provide</h2>
                        <p className="text-sm text-soil-400 mb-4">
                            Landlords typically ask for these once they review your application - no need to upload anything now.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {requirements.map((req) => (
                                <div key={req.type} className="flex items-center space-x-2 p-3 bg-soil-50 rounded-lg">
                                    <FileText className="w-4 h-4 text-highland-500 flex-shrink-0" />
                                    <span className="text-sm text-soil-600">{REQUIREMENT_LABELS[req.type] || req.type}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PropertyApplication;
